import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { businessInfo, deviceTypes, translations } from '../data/mock';
import { Phone, Mail, MapPin, Clock, Upload, X, CheckCircle, Send } from 'lucide-react';
import { toast } from 'sonner';

const ContactSection = () => {
  const fileInputRef = useRef(null);
  const recaptchaRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const t = translations.contact;
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.reviviz.cz';
  const apiKey = process.env.REACT_APP_API_KEY;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    deviceType: '',
    description: '',
    files: []
  });
  const [errors, setErrors] = useState({});
  const [recaptchaToken, setRecaptchaToken] = useState('');

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t.errors.nameRequired;
    if (!formData.email.trim() && !formData.phone.trim()) {
      newErrors.contact = t.errors.contactRequired;
    }
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t.errors.invalidEmail;
    }
    if (!formData.deviceType) newErrors.deviceType = t.errors.deviceTypeRequired;
    if (!formData.description.trim()) newErrors.description = t.errors.descriptionRequired;
    if (!recaptchaToken) newErrors.recaptcha = 'Please complete the reCAPTCHA.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (!apiKey) {
      toast.error('Missing API key. Please set REACT_APP_API_KEY.');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = new FormData();
      payload.append('name', formData.name);
      payload.append('email', formData.email);
      if (formData.phone) payload.append('phone', formData.phone);
      payload.append('message', formData.description);
      payload.append('subject', 'Zarizeni: ' + formData.deviceType);
      payload.append('recaptchaToken', recaptchaToken);
      formData.files.forEach((file) => payload.append('files', file));

      const response = await fetch(`${apiBaseUrl}/v1/forms/contact`, {
        method: 'POST',
        headers: {
          'x-api-key': apiKey,
        },
        body: payload,
      });

      const responseBody = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = responseBody?.errors?.join(' ') || 'Odeslání selhalo.';
        throw new Error(message);
      }

      setIsSubmitted(true);
      toast.success('Pozadavek byl uspesne odeslan! Brzy vas budeme kontaktovat.');

      setTimeout(() => {
        setIsSubmitted(false);
        setRecaptchaToken('');
        recaptchaRef.current?.reset();
        setFormData({
          name: '',
          email: '',
          phone: '',
          deviceType: '',
          description: '',
          files: []
        });
      }, 3000);
    } catch (err) {
      toast.error(err?.message || 'Odeslání selhalo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const validFiles = newFiles.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isSmallEnough = file.size <= 10 * 1024 * 1024; // 10MB
      return isImage && isSmallEnough;
    });

    if (validFiles.length !== newFiles.length) {
      toast.error('Některé soubory byly přeskočeny. Povoleny jsou pouze obrázky do 10 MB.');
    }

    setFormData(prev => ({
      ...prev,
      files: [...prev.files, ...validFiles].slice(0, 5) // Max 5 files
    }));
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  return (
    <section id="contact" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left - Contact Info */}
          <div>
            <span className="inline-block text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              {t.label}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              {t.title}
            </h2>
            <p className="text-lg text-gray-600 mb-10 leading-relaxed">
              {t.description}
            </p>

            {/* Contact Methods */}
            <div className="space-y-6">
              <a
                href={`tel:${businessInfo.phone}`}
                className="flex items-center gap-4 p-4 bg-white rounded-xl hover:shadow-md transition-shadow group"
              >
                <div className="w-12 h-12 bg-gray-100 group-hover:bg-gray-900 rounded-xl flex items-center justify-center transition-colors">
                  <Phone className="w-5 h-5 text-gray-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t.callUs}</p>
                  <p className="font-semibold text-gray-900">{businessInfo.phone}</p>
                </div>
              </a>

              <a
                href={`mailto:${businessInfo.email}`}
                className="flex items-center gap-4 p-4 bg-white rounded-xl hover:shadow-md transition-shadow group"
              >
                <div className="w-12 h-12 bg-gray-100 group-hover:bg-gray-900 rounded-xl flex items-center justify-center transition-colors">
                  <Mail className="w-5 h-5 text-gray-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t.emailUs}</p>
                  <p className="font-semibold text-gray-900">{businessInfo.email}</p>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 bg-white rounded-xl">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t.visitUs}</p>
                  <p className="font-semibold text-gray-900">{businessInfo.address}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-white rounded-xl">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t.openingHours}</p>
                  <p className="font-semibold text-gray-900">{businessInfo.hours}</p>
                </div>
              </div>
            </div>

            {/* Google Maps */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t.map.title}</h3>
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2607.0!2d17.2508!3d49.5938!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47124e8c9f2a5c5d%3A0x1!2sHybe%C5%A1ova%20200%2F6%2C%20779%2000%20Olomouc!5e0!3m2!1scs!2scz!4v1704067200000!5m2!1scs!2scz"
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mapa - reviviz Olomouc"
                ></iframe>
              </div>
            </div>

            {/* Free Diagnostics Badge */}
            <div className="mt-8 p-6 bg-gray-900 rounded-2xl text-white">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
                  <span className="text-2xl font-bold">0</span>
                </div>
                <div>
                  <p className="font-semibold text-lg">{t.freeDiagnostics}</p>
                  <p className="text-gray-400">{t.freeDiagnosticsDesc}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-xl shadow-gray-200/50">
            {isSubmitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="w-10 h-10 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{t.success.title}</h3>
                <p className="text-gray-600 max-w-sm">
                  {t.success.message}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="text-center mb-8">
                  <h3 className="text-xl font-bold text-gray-900">{t.form.title}</h3>
                  <p className="text-gray-500 mt-1">{t.form.subtitle}</p>
                </div>

                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">{t.form.name}</Label>
                  <Input
                    id="name"
                    placeholder={t.form.namePlaceholder}
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className={errors.name ? 'border-red-300' : ''}
                  />
                  {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
                </div>

                {/* Email & Phone */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t.form.email}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={t.form.emailPlaceholder}
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className={errors.email ? 'border-red-300' : ''}
                    />
                    {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t.form.phone}</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder={t.form.phonePlaceholder}
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                </div>
                {errors.contact && <p className="text-sm text-red-500">{errors.contact}</p>}

                {/* Device Type */}
                <div className="space-y-2">
                  <Label>{t.form.deviceType}</Label>
                  <Select
                    value={formData.deviceType}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, deviceType: value }))}
                  >
                    <SelectTrigger className={errors.deviceType ? 'border-red-300' : ''}>
                      <SelectValue placeholder={t.form.deviceTypePlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {deviceTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.deviceType && <p className="text-sm text-red-500">{errors.deviceType}</p>}
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">{t.form.description}</Label>
                  <Textarea
                    id="description"
                    placeholder={t.form.descriptionPlaceholder}
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className={errors.description ? 'border-red-300' : ''}
                  />
                  {errors.description && <p className="text-sm text-red-500">{errors.description}</p>}
                </div>

                {/* File Upload */}
                <div className="space-y-2">
                  <Label>{t.form.attachPhotos}</Label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-gray-300 transition-colors"
                  >
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">{t.form.uploadText}</p>
                    <p className="text-xs text-gray-400 mt-1">{t.form.uploadHint}</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                  />

                  {/* File Preview */}
                  {formData.files.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {formData.files.map((file, index) => (
                        <div
                          key={index}
                          className="relative group bg-gray-100 rounded-lg p-2 pr-8"
                        >
                          <span className="text-sm text-gray-600 truncate max-w-[150px] inline-block">
                            {file.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="absolute right-1 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded"
                          >
                            <X className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* reCAPTCHA */}
                <div className="space-y-2">
                  {recaptchaSiteKey ? (
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={recaptchaSiteKey}
                      onChange={(token) => {
                        setRecaptchaToken(token || '');
                        if (errors.recaptcha) {
                          setErrors((prev) => ({ ...prev, recaptcha: undefined }));
                        }
                      }}
                      onExpired={() => setRecaptchaToken('')}
                    />
                  ) : (
                    <p className="text-sm text-red-500">
                      Missing reCAPTCHA site key. Set REACT_APP_RECAPTCHA_SITE_KEY.
                    </p>
                  )}
                  {errors.recaptcha && <p className="text-sm text-red-500">{errors.recaptcha}</p>}
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white py-6 text-lg"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t.form.sending}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {t.form.submit}
                      <Send className="w-5 h-5" />
                    </span>
                  )}
                </Button>

                <p className="text-center text-sm text-gray-500">
                  {t.form.freeNote} <span className="font-semibold text-emerald-600">{t.form.freeHighlight}</span>{t.form.noCommitment}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;

